import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { TankFilterValues } from '@components/Common/EntryFilters/TankSelectorControlled';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_TERMINAL = 'terminal';
const PARAM_TANK = 'tank';
const PARAM_TO = 'to';

export interface ApprovalFilters {
  terminalId: string | null;
  tankId: string | null;
  endDate: string | null;
}

export interface UseApprovalFiltersResult {
  filters: ApprovalFilters;
  tankFilter: TankFilterValues;
  dateFilter: DateFilterValues;
  setTankFilter: (value: TankFilterValues) => void;
  setDateFilter: (value: DateFilterValues) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage approval filters via URL search params.
 *
 * URL format: /approval?terminal=xxx&tank=xxx&to=2024-01-31
 *
 * Note: Start date is not used in approval (it's set by the last approval date)
 */
export function useApprovalFilters(): UseApprovalFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<ApprovalFilters>(() => {
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const tankId = searchParams.get(PARAM_TANK);
    const endDate = searchParams.get(PARAM_TO);

    return {
      terminalId,
      tankId,
      endDate,
    };
  }, [searchParams]);

  // Tank filter for the controlled component
  const tankFilter = useMemo<TankFilterValues>(() => ({
    terminalId: filters.terminalId,
    tankId: filters.tankId,
  }), [filters.terminalId, filters.tankId]);

  // Date filter for the controlled component (start date always null for approval)
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: null,
    endDate: filters.endDate,
  }), [filters.endDate]);

  // Update tank filter in URL
  const setTankFilter = useCallback((value: TankFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.terminalId) {
        newParams.set(PARAM_TERMINAL, value.terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      if (value.tankId) {
        newParams.set(PARAM_TANK, value.tankId);
      } else {
        newParams.delete(PARAM_TANK);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL (only end date for approval)
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setDateFilter,
    clearFilters,
  };
}

export default useApprovalFilters;
